import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
    Avatar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WalletIcon from "@mui/icons-material/Wallet";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ColorModeSelect from "shared/theme/ColorModeSelect";
import LanguageSelector from "shared/ui/LanguageSelector";
import { useActiveHeader, useFinancialVisibility, useUserSession } from "shared/contexts/UserContext";
import logo from "assets/misc/logo512.png";
import { useTranslation } from "shared/hooks/useTranslation";
import { useBreakpoint } from "shared/hooks/useBreakpoint";

export const DESKTOP_DRAWER_WIDTH = 224;
export const MOBILE_BOTTOM_NAV_HEIGHT = 72;
const MOBILE_MENU_PANEL_ID = "mobile-menu-panel";

const ResponsiveNavbar: React.FC = () => {
    const { isDesktop } = useBreakpoint();
    const { t, getPathWithLanguage } = useTranslation();

    const { profile, resetUser, isLogged } = useUserSession();
    const { activeHeader } = useActiveHeader();
    const { balancesVisible, setBalancesVisible } = useFinancialVisibility();
    const loggedIn = isLogged();
    const location = useLocation();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

    const handleLogOff = useCallback(() => {
        try {
            if (profile?.username) window.sessionStorage.removeItem(`pending-card-summary-import:${profile.username}`);
        } catch (_) {}
        resetUser();
        window.location.reload();
    }, [profile?.username, resetUser]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    type NavItem = { label: string; to: string; icon: React.ReactNode; section: "overview" | "investments" | "account" };
    const navigationItems: NavItem[] = useMemo(() => {
        if (!loggedIn) return [];
        const base = `/${profile?.username ?? ""}`;
        return [
            { label: t('navigation.personal'), to: getPathWithLanguage(`${base}/personal`), icon: <AccountBalanceWalletIcon fontSize="small" />, section: "overview" },
            { label: t('navigation.groups'), to: getPathWithLanguage(`${base}/groups`), icon: <GroupsIcon fontSize="small" />, section: "overview" },
            { label: t('navigation.wallet'), to: getPathWithLanguage(`${base}/accounts`), icon: <WalletIcon fontSize="small" />, section: "overview" },
            { label: t('navigation.cotizaciones'), to: getPathWithLanguage(`${base}/cotizaciones`), icon: <ShowChartIcon fontSize="small" />, section: "investments" },
            { label: t('navigation.portfolio'), to: getPathWithLanguage(`${base}/investments`), icon: <PieChartIcon fontSize="small" />, section: "investments" },
            { label: t('navigation.profile'), to: getPathWithLanguage(`${base}/profile`), icon: <AccountCircleIcon fontSize="small" />, section: "account" },
        ];
    }, [loggedIn, profile?.username, t, getPathWithLanguage]);

    const sectionLabels: Record<NavItem["section"], string> = useMemo(() => ({
        overview: t('navigation.sectionOverview'),
        investments: t('navigation.sectionInvestments'),
        account: t('navigation.sectionAccount'),
    }), [t]);

    const groupedNavItems = useMemo(() => {
        const order: NavItem["section"][] = ["overview", "investments", "account"];
        return order.map((section) => ({
            section,
            label: sectionLabels[section],
            items: navigationItems.filter((item) => item.section === section),
        }));
    }, [navigationItems, sectionLabels]);

    const mobileNavItems = useMemo(() => {
        if (!loggedIn) return [];
        const base = `/${profile?.username ?? ""}`;
        return [
            { label: t('navigation.personal'), to: getPathWithLanguage(`${base}/personal`), icon: <AccountBalanceWalletIcon fontSize="small" /> },
            { label: t('navigation.groups'), to: getPathWithLanguage(`${base}/groups`), icon: <GroupsIcon fontSize="small" /> },
            { label: t('navigation.wallet'), to: getPathWithLanguage(`${base}/accounts`), icon: <WalletIcon fontSize="small" /> },
        ];
    }, [loggedIn, profile?.username, t, getPathWithLanguage]);

    const isActiveRoute = useCallback(
        (to: string, exact = false) => {
            const current = location.pathname;
            const aliases = to.endsWith("/accounts") ? [to, to.replace(/\/accounts$/, "/wallet")] : [to];
            return aliases.some((target) => exact ? current === target : current === target || current.startsWith(`${target}/`));
        },
        [location.pathname],
    );

    const isMoreRouteActive = useCallback(() => {
        const base = getPathWithLanguage(`/${profile?.username ?? ""}`);
        return location.pathname === base || ["profile", "cotizaciones", "investments"].some((section) =>
            location.pathname === `${base}/${section}` || location.pathname.startsWith(`${base}/${section}/`),
        );
    }, [getPathWithLanguage, location.pathname, profile?.username]);

    const drawerContent = useMemo(() => {
        if (!loggedIn) {
            const loginPath = getPathWithLanguage("login");
            const registerPath = getPathWithLanguage("register");
            return (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }} role="presentation">
                    <Box
                        sx={{ px: 2, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}
                        data-onboarding-id="nav-brand"
                    >
                        <Avatar src={logo} sx={{ width: 40, height: 40 }} alt="Logo" />
                        <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>Sí, Hay Plata</Typography>
                        {!isDesktop && (
                            <IconButton
                                size="small"
                                onClick={() => setIsMobileMenuOpen(false)}
                                aria-label={t('common.closeMenu')}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>

                    <Divider />

                    <List sx={{ flexGrow: 1, py: 1 }}>
                        <ListItemButton component={RouterLink} to={loginPath} sx={{ borderRadius: 1, mx: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <LoginIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t("pages.signIn.signIn")} />
                        </ListItemButton>
                        <ListItemButton component={RouterLink} to={registerPath} sx={{ borderRadius: 1, mx: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <PersonAddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t("pages.signUp.signUp")} />
                        </ListItemButton>
                    </List>

                    <Divider />

                    <Box sx={{ px: 2, py: 2 }}>
                        <Stack direction="row" spacing={1} justifyContent="space-around" alignItems="center">
                            <ColorModeSelect />
                            <LanguageSelector />
                        </Stack>
                    </Box>
                </Box>
            );
        }

        return (
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }} role="presentation">
                <Box
                    sx={{ px: 2, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}
                    data-onboarding-id="nav-brand"
                >
                    <Avatar src={logo} sx={{ width: 40, height: 40 }} alt="Logo" />
                    <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>Sí, Hay Plata</Typography>
                    {!isDesktop && (
                        <IconButton
                            size="small"
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-label={t('common.closeMenu')}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                <Divider />

                <List sx={{ flexGrow: 1, py: 1 }}>
                    {groupedNavItems.map(({ section, label, items }) => (
                        <React.Fragment key={section}>
                            <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>
                                {label}
                            </ListSubheader>
                            {items.map((item) => (
                                <ListItemButton
                                    key={item.label}
                                    component={RouterLink}
                                    to={item.to}
                                    selected={isActiveRoute(item.to)}
                                    aria-current={isActiveRoute(item.to) ? "page" : undefined}
                                    sx={{
                                        borderRadius: 2, mx: 1, minHeight: 44,
                                        "&.Mui-selected": { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.1), color: "primary.main", fontWeight: 700 },
                                        "&.Mui-selected .MuiListItemIcon-root": { color: "primary.main" },
                                    }}
                                    data-onboarding-id={item.section === "overview" && item.to.endsWith("/groups")
                                        ? "nav-groups"
                                        : item.section === "account" ? "nav-profile" : undefined}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.label} />
                                </ListItemButton>
                            ))}
                        </React.Fragment>
                    ))}
                </List>

                <Divider />

                <Box sx={{ px: 2, py: 2 }}>
                    <ListItemButton
                        component={RouterLink}
                        to={getPathWithLanguage(`/${profile?.username ?? ""}/profile`)}
                        sx={{ borderRadius: 1 }}
                        data-onboarding-id="nav-profile"
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Avatar src={profile?.profilePicture} sx={{ width: 28, height: 28 }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={profile?.username ?? t('common.user')}
                            secondary={activeHeader?.name || undefined}
                            secondaryTypographyProps={{ noWrap: true }}
                        />
                    </ListItemButton>

                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogOff}
                        sx={{ mb: 1 }}
                    >
                        {t('common.signOut')}
                    </Button>
                </Box>

                <Divider />

                <Box sx={{ px: 2, py: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} justifyContent="space-around" alignItems="center">
                        <Tooltip title={balancesVisible ? t('common.hideBalances') : t('common.showBalances')}>
                            <IconButton
                                size="small"
                                onClick={() => setBalancesVisible(!balancesVisible)}
                                aria-label={balancesVisible ? t('common.hideBalances') : t('common.showBalances')}
                            >
                                {balancesVisible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <ColorModeSelect />
                        <LanguageSelector />
                    </Stack>
                </Box>
            </Box>
        );
    }, [loggedIn, groupedNavItems, profile?.username, profile?.profilePicture, activeHeader?.name, handleLogOff, t, getPathWithLanguage, balancesVisible, setBalancesVisible, isDesktop, isActiveRoute]);

    return (
        <>
            {isDesktop && (
                <Drawer
                    variant="permanent"
                    slotProps={{
                        paper: {
                            elevation: 8,
                            sx: { width: DESKTOP_DRAWER_WIDTH, boxSizing: "border-box" },
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {!isDesktop && (
                <>
                    <Box
                        component="nav"
                        aria-label={t("common.mainNavigation", { defaultValue: "Navegación principal" })}
                        sx={{
                            position: "fixed",
                            left: { xs: 12, sm: 24 },
                            right: { xs: 12, sm: 24 },
                            bottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
                            zIndex: (theme) => theme.zIndex.appBar,
                            display: "flex",
                            justifyContent: "center",
                            pointerEvents: "none",
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: 520,
                                minHeight: 72,
                                px: 0.75,
                                py: 0.75,
                                borderRadius: 999,
                                display: "grid",
                                gridTemplateColumns: loggedIn ? "repeat(4, minmax(0, 1fr))" : "48px minmax(0, 1fr) 48px",
                                alignItems: "center",
                                gap: 0.5,
                                pointerEvents: "auto",
                                color: "text.primary",
                                bgcolor: (theme) => alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.82 : 0.9),
                                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.16)}`,
                                boxShadow: (theme) => theme.palette.mode === "dark"
                                    ? `0 18px 42px ${alpha(theme.palette.common.black, 0.38)}`
                                    : `0 18px 42px ${alpha(theme.palette.common.black, 0.14)}`,
                                backdropFilter: "blur(18px)",
                            }}
                        >
                            {loggedIn ? (
                                <>
                                    {mobileNavItems.map((item) => {
                                        const isMore = item.to === "#menu";
                                        const active = isMore ? isMoreRouteActive() : isActiveRoute(item.to);
                                        return (
                                            <Box
                                                key={item.to}
                                                component={isMore ? "button" : RouterLink}
                                                {...(isMore ? { type: "button", onClick: () => setIsMobileMenuOpen(true), "aria-controls": MOBILE_MENU_PANEL_ID, "aria-expanded": isMobileMenuOpen } : { to: item.to })}
                                                aria-current={active ? "page" : undefined}
                                                data-onboarding-id={item.to.endsWith("/groups") ? "nav-groups" : undefined}
                                                sx={{
                                                    minWidth: 0,
                                                    minHeight: 56,
                                                    border: 0,
                                                    background: "transparent",
                                                    cursor: "pointer",
                                                    font: "inherit",
                                                    borderRadius: 999,
                                                    px: 0.5,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 0.25,
                                                    color: active ? "primary.contrastText" : "text.secondary",
                                                    textDecoration: "none",
                                                    bgcolor: active ? "primary.main" : "transparent",
                                                    boxShadow: active ? 4 : 0,
                                                    transition: "background-color 160ms ease, color 160ms ease, box-shadow 160ms ease",
                                                    "&:hover": {
                                                        bgcolor: active ? "primary.main" : (theme) => alpha(theme.palette.primary.main, 0.08),
                                                    },
                                                }}
                                            >
                                                {item.icon}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: 11,
                                                        lineHeight: 1.1,
                                                        fontWeight: active ? 850 : 750,
                                                        maxWidth: "100%",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                            </Box>
                                        );
                                    })}

                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => setIsMobileMenuOpen(true)}
                                        aria-label={t('common.openMenu')}
                                        aria-controls={MOBILE_MENU_PANEL_ID}
                                        aria-expanded={isMobileMenuOpen ? "true" : undefined}
                                        aria-haspopup="menu"
                                        aria-current={isMoreRouteActive() ? "page" : undefined}
                                        data-onboarding-id="nav-profile"
                                        sx={{
                                            minWidth: 0,
                                            minHeight: 58,
                                            border: 0,
                                            borderRadius: 999,
                                            px: 0.5,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.25,
                                            color: isMoreRouteActive() ? "primary.contrastText" : "text.secondary",
                                            bgcolor: isMoreRouteActive() ? "primary.main" : "transparent",
                                            boxShadow: isMoreRouteActive() ? 4 : 0,
                                            cursor: "pointer",
                                            font: "inherit",
                                            "&:hover": {
                                                bgcolor: (theme) => isMoreRouteActive()
                                                    ? theme.palette.primary.main
                                                    : alpha(theme.palette.primary.main, 0.08),
                                            },
                                        }}
                                    >
                                        <Avatar
                                            src={profile?.profilePicture}
                                            alt={profile?.username ?? t('common.profile')}
                                            sx={{ width: 24, height: 24 }}
                                        />
                                                <Typography component="span" sx={{ fontSize: 11, lineHeight: 1.1, fontWeight: 750 }}>
                                            {t('navigation.menu')}
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Box
                                        component={RouterLink}
                                        to={getPathWithLanguage("landing")}
                                        aria-label="Sí, Hay Plata"
                                        sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 56, borderRadius: 999 }}
                                    >
                                        <Avatar src={logo} alt="Logo" sx={{ width: 32, height: 32 }} />
                                    </Box>
                                    <Button component={RouterLink} to={getPathWithLanguage("login")} variant="contained" sx={{ minHeight: 48, minWidth: 0, px: 1, borderRadius: 999, whiteSpace: "nowrap" }}>
                                        {t("pages.signIn.signIn")}
                                    </Button>
                                    <IconButton
                                        onClick={() => setIsMobileMenuOpen(true)}
                                        aria-label={t('common.openMenu')}
                                        aria-controls={MOBILE_MENU_PANEL_ID}
                                        aria-expanded={isMobileMenuOpen ? "true" : undefined}
                                        aria-haspopup="menu"
                                        sx={{ minHeight: 48, borderRadius: 999 }}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    </Box>

                    <Drawer
                        anchor="bottom"
                        variant="temporary"
                        open={isMobileMenuOpen}
                        onClose={() => setIsMobileMenuOpen(false)}
                        slotProps={{
                            paper: {
                                id: MOBILE_MENU_PANEL_ID,
                                elevation: 8,
                                sx: {
                                    width: "100%",
                                    maxWidth: "100%",
                                    maxHeight: "82vh",
                                    boxSizing: "border-box",
                                    borderTopLeftRadius: 24,
                                    borderTopRightRadius: 24,
                                    overflow: "hidden",
                                    bgcolor: "background.paper",
                                },
                            },
                        }}
                    >
                        {drawerContent}
                    </Drawer>
                </>
            )}
        </>
    );
};

export default ResponsiveNavbar;
